// AdminOverview ("/admin"). Regina's top-level dashboard with alerts banner,
// 3 KPI tiles, and the employee-vs-project toggle.

import { useState, useMemo } from 'react';
import { NOW, parseDate, isOverdue, fmtDate } from '../lib/dates.ts';
import { navigate } from '../lib/routing.js';
import { Icon } from '../components/Icon.jsx';
import { Avatar } from '../components/Avatar.jsx';
import { ProjectView } from '../components/ProjectView.jsx';

export const AdminOverview = ({ user, tasks, staff, groups = [], onCreate }) => {
  const [adminView, setAdminView] = useState(() => localStorage.getItem('vava_admin_view') || 'project');
  function switchAdminView(v) { setAdminView(v); localStorage.setItem('vava_admin_view', v); }
  const now = NOW;
  const stats = useMemo(() => {
    const overdue = tasks.filter(isOverdue);
    const blocked = tasks.filter(t => t.status === '阻塞');
    const blockedLong = blocked.filter(t => t.updates && t.updates.length &&
      (now - parseDate(t.updates[0].at)) / (1000 * 60 * 60 * 24) > 7);
    const silent = staff.filter(s => {
      if (s.role === 'admin') return false;
      const latest = tasks.filter(t => t.assigneeId === s.id)
        .flatMap(t => (t.updates || []).map(u => new Date(u.at)))
        .sort((a, b) => b - a)[0];
      return !latest || (now - latest) / (1000 * 60 * 60 * 24) > 5;
    });
    return { overdue, blocked, blockedLong, silent };
  }, [tasks, staff, now]);

  // Month boundaries for this-month vs last-month split
  const thisMonthStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const lastMonthStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
  function inMonth(iso, monthStart) {
    if (!iso) return false;
    const d = new Date(iso);
    return d >= monthStart && d < new Date(Date.UTC(monthStart.getUTCFullYear(), monthStart.getUTCMonth() + 1, 1));
  }

  const perStaff = staff.filter(s => s.role !== 'admin').map(s => {
    const mine = tasks.filter(t => t.assigneeId === s.id);
    // This-month: tasks with completed_at in this month
    const thisDone = mine.filter(t => inMonth(t.completedAt, thisMonthStart)).length;
    const thisDue = mine.filter(t => inMonth(t.dueDate, thisMonthStart)).length;
    const lastDone = mine.filter(t => inMonth(t.completedAt, lastMonthStart)).length;
    const lastDue = mine.filter(t => inMonth(t.dueDate, lastMonthStart)).length;
    const thisPct = thisDue ? Math.round((thisDone / thisDue) * 100) : null;
    const lastPct = lastDue ? Math.round((lastDone / lastDue) * 100) : null;
    return {
      staff: s,
      total: mine.length,
      todo: mine.filter(t => t.status === '待开始').length,
      inprog: mine.filter(t => t.status === '进行中').length,
      blocked: mine.filter(t => t.status === '阻塞').length,
      done: mine.filter(t => t.status === '已完成').length,
      overdue: mine.filter(isOverdue).length,
      thisDone, thisDue, thisPct, lastDone, lastDue, lastPct,
      // Lifetime % retained as fallback when month data is null
      pct: mine.length ? Math.round(mine.filter(t => t.status === '已完成').length / mine.length * 100) : 0
    };
  }).sort((a, b) => b.total - a.total);

  const totals = {
    total: tasks.length,
    done: tasks.filter(t => t.status === '已完成').length,
    inprog: tasks.filter(t => t.status === '进行中').length,
    blocked: tasks.filter(t => t.status === '阻塞').length
  };
  const pct = totals.total ? Math.round(totals.done / totals.total * 100) : 0;

  return (
    <>
      <div className="vava-page-header">
        <div>
          <h1 className="vava-page-title">待办总览</h1>
          <div className="vava-page-meta">{fmtDate(now.toISOString().slice(0, 10))}</div>
        </div>
        <button className="btn btn-primary" onClick={onCreate}>
          <Icon name="plus"/>新建待办
        </button>
      </div>

      {stats.overdue.length > 0 && (
        <div className="vava-alert red">
          <div className="left"><Icon name="alert" className="icon"/><span><strong className="mono">{stats.overdue.length}</strong> 个待办已逾期</span></div>
          <a className="action" href="#" onClick={(e) => { e.preventDefault(); document.getElementById('emp-table')?.scrollIntoView({ behavior: 'smooth' }); }}>查看 →</a>
        </div>
      )}
      {stats.blockedLong.length > 0 && (
        <div className="vava-alert amber">
          <div className="left"><Icon name="alert" className="icon"/><span><strong className="mono">{stats.blockedLong.length}</strong> 个待办阻塞超 7 天</span></div>
          <a className="action" href="#" onClick={(e) => { e.preventDefault(); }}>查看 →</a>
        </div>
      )}
      {stats.silent.length > 0 && (
        <div className="vava-alert amber">
          <div className="left"><Icon name="alert" className="icon"/><span><strong className="mono">{stats.silent.length}</strong> 名员工超 5 天无更新：{stats.silent.map(s => s.name).join(', ')}</span></div>
          <a className="action" href="#" onClick={(e) => { e.preventDefault(); }}>查看 →</a>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, margin: '16px 0 20px 0' }} className="admin-kpis">
        <div className="kpi-card">
          <div className="label">全员待办</div>
          <div className="big mono">{totals.total}</div>
          <div className="sub"><span>本月 {totals.done} 完成</span><span>{totals.inprog} 进行中</span></div>
        </div>
        <div className="kpi-card">
          <div className="label">完成率</div>
          <div className="big mono">{pct}%</div>
          <div className="sub"><span style={{ color: 'var(--vava-green)' }}>●</span>{totals.done}/{totals.total}</div>
        </div>
        <div className="kpi-card">
          <div className="label">需要关注</div>
          <div className="big mono" style={{ color: 'var(--vava-red)' }}>{stats.overdue.length + stats.blockedLong.length}</div>
          <div className="sub"><span>{stats.overdue.length} 逾期</span><span>{stats.blockedLong.length} 阻塞&gt;7天</span></div>
        </div>
      </div>

      <div className="vava-card" id="emp-table" style={{ padding: 0 }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--vava-border-soft)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <h2 className="vava-card-title" style={{ margin: 0 }}>
            {adminView === 'project' ? '待办视图（按分类）' : '员工进度'}
          </h2>
          <div className="view-toggle" role="tablist" aria-label="总览视图切换">
            <button className={adminView === 'project' ? 'active' : ''}
                    role="tab" aria-selected={adminView === 'project'}
                    onClick={() => switchAdminView('project')}>待办视图</button>
            <button className={adminView === 'staff' ? 'active' : ''}
                    role="tab" aria-selected={adminView === 'staff'}
                    onClick={() => switchAdminView('staff')}>个人视图</button>
          </div>
        </div>
        {adminView === 'staff' ? (
          <table className="vava-table">
            <thead>
              <tr>
                <th>员工</th>
                <th style={{ width: 70, textAlign: 'right' }}>待开始</th>
                <th style={{ width: 70, textAlign: 'right' }}>进行中</th>
                <th style={{ width: 70, textAlign: 'right' }}>阻塞</th>
                <th style={{ width: 70, textAlign: 'right' }}>已完成</th>
                <th style={{ width: 70, textAlign: 'right' }}>逾期</th>
                <th style={{ width: 110, textAlign: 'right' }} title="本月完成 / 应到期">本月</th>
                <th style={{ width: 110, textAlign: 'right' }} title="上月完成 / 应到期">上月</th>
                <th style={{ width: 80, textAlign: 'right' }}>累计%</th>
              </tr>
            </thead>
            <tbody>
              {perStaff.map(r => {
                const highlight = r.total >= 13;
                return (
                  <tr key={r.staff.id} className={highlight ? 'highlight' : ''}
                      onClick={() => navigate('/people/' + r.staff.id)}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Avatar staff={r.staff}/>
                        <div>
                          <div style={{ fontWeight: 600 }}>{r.staff.name}</div>
                          <div style={{ fontSize: 11, color: 'var(--vava-text-muted)' }}>{r.staff.title}</div>
                        </div>
                      </div>
                    </td>
                    <td className="mono" style={{ textAlign: 'right' }}>{r.todo || '—'}</td>
                    <td className="mono" style={{ textAlign: 'right' }}>{r.inprog || '—'}</td>
                    <td className="mono" style={{ textAlign: 'right', color: r.blocked ? 'var(--vava-red)' : 'inherit' }}>{r.blocked || '—'}</td>
                    <td className="mono" style={{ textAlign: 'right', color: r.done ? 'var(--vava-green)' : 'inherit' }}>{r.done || '—'}</td>
                    <td className="mono" style={{ textAlign: 'right', color: r.overdue ? 'var(--vava-red)' : 'inherit', fontWeight: r.overdue ? 600 : 400 }}>{r.overdue || '—'}</td>
                    <td className="mono" style={{ textAlign: 'right' }}>
                      <span style={{ color: 'var(--vava-text)' }}>{r.thisDone}/{r.thisDue || '—'}</span>
                      {r.thisPct !== null && (
                        <span style={{ marginLeft: 4, color: r.thisPct >= 80 ? 'var(--vava-green)' : r.thisPct >= 50 ? 'var(--vava-amber)' : 'var(--vava-red)' }}>
                          {' '}{r.thisPct}%
                        </span>
                      )}
                    </td>
                    <td className="mono" style={{ textAlign: 'right', color: 'var(--vava-text-subtle)', fontSize: 12 }}>
                      <span>{r.lastDone}/{r.lastDue || '—'}</span>
                      {r.lastPct !== null && <span>{' '}{r.lastPct}%</span>}
                    </td>
                    <td className="mono" style={{ textAlign: 'right' }}>
                      <span style={{ fontSize: 13 }}>{r.pct}%</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div style={{ padding: 16 }}>
            <ProjectView tasks={tasks} staff={staff} groups={groups} />
          </div>
        )}
      </div>
    </>
  );
};
